// Maps ISO 3166-1 alpha-2 country codes to a representative IANA timezone.
// Used so date/time calculations follow the store's country rather than the
// device's local timezone.
const COUNTRY_TIMEZONE = {
    SG: 'Asia/Singapore',
    MY: 'Asia/Kuala_Lumpur',
    ID: 'Asia/Jakarta',
    TH: 'Asia/Bangkok',
    PH: 'Asia/Manila',
    VN: 'Asia/Ho_Chi_Minh',
    BN: 'Asia/Brunei',
    KH: 'Asia/Phnom_Penh',
    LA: 'Asia/Vientiane',
    MM: 'Asia/Yangon',
    IN: 'Asia/Kolkata',
    AE: 'Asia/Dubai',
    SA: 'Asia/Riyadh',
    QA: 'Asia/Qatar',
    KW: 'Asia/Kuwait',
    BH: 'Asia/Bahrain',
    OM: 'Asia/Muscat',
    HK: 'Asia/Hong_Kong',
    MO: 'Asia/Macau',
    TW: 'Asia/Taipei',
    JP: 'Asia/Tokyo',
    KR: 'Asia/Seoul',
    CN: 'Asia/Shanghai',
    AU: 'Australia/Sydney',
    NZ: 'Pacific/Auckland',
    GB: 'Europe/London',
    IE: 'Europe/Dublin',
    DE: 'Europe/Berlin',
    FR: 'Europe/Paris',
    IT: 'Europe/Rome',
    ES: 'Europe/Madrid',
    PT: 'Europe/Lisbon',
    NL: 'Europe/Amsterdam',
    BE: 'Europe/Brussels',
    CH: 'Europe/Zurich',
    AT: 'Europe/Vienna',
    SE: 'Europe/Stockholm',
    NO: 'Europe/Oslo',
    DK: 'Europe/Copenhagen',
    FI: 'Europe/Helsinki',
    PL: 'Europe/Warsaw',
    RU: 'Europe/Moscow',
    TR: 'Europe/Istanbul',
    US: 'America/New_York',
    CA: 'America/Toronto',
    MX: 'America/Mexico_City',
    BR: 'America/Sao_Paulo',
    AR: 'America/Argentina/Buenos_Aires',
    CL: 'America/Santiago',
    CO: 'America/Bogota',
    PE: 'America/Lima',
    ZA: 'Africa/Johannesburg',
    EG: 'Africa/Cairo',
    NG: 'Africa/Lagos',
    KE: 'Africa/Nairobi',
};

export const getTimezoneByCountry = (countryCode) => {
    if (!countryCode) return undefined;
    return COUNTRY_TIMEZONE[String(countryCode).toUpperCase()];
};

const DEFAULT_STORE_COUNTRY_CODE = 'SG';

export const resolveStoreCountryCode = (countryCode, fallback = DEFAULT_STORE_COUNTRY_CODE) => {
    if (getTimezoneByCountry(countryCode)) {
        return String(countryCode).toUpperCase();
    }
    return fallback;
};

// Extracts the calendar parts (year, month, day, weekday, hour, minute) of the
// given moment as observed in the country's timezone. Falls back to SG when the
// country code is unknown.
const getDatePartsInTimezone = (countryCode, date = new Date()) => {
    const resolvedCountryCode = resolveStoreCountryCode(countryCode);
    const timeZone = getTimezoneByCountry(resolvedCountryCode);

    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    const parts = formatter.formatToParts(date).reduce((acc, part) => {
        if (part.type !== 'literal') acc[part.type] = part.value;
        return acc;
    }, {});

    return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        weekday: parts.weekday,
        // Some implementations return '24' for midnight when hour12 is false.
        hour: parts.hour === '24' ? '00' : parts.hour,
        minute: parts.minute,
    };
};

export const getStoreDateString = (countryCode, date = new Date()) => {
    const { year, month, day } = getDatePartsInTimezone(countryCode, date);
    return `${day}-${month}-${year}`;
};

export const getStoreToday = (countryCode, date = new Date()) => {
    const { weekday } = getDatePartsInTimezone(countryCode, date);
    return weekday.toLowerCase();
};

export const getStoreCurrentTime = (countryCode, date = new Date()) => {
    const { hour, minute } = getDatePartsInTimezone(countryCode, date);
    return `${hour}:${minute}`;
};

// Returns a `Date` object whose calendar parts (year, month, day, hour, minute)
// in the device's local timezone match the store's current calendar parts.
// Useful when feeding into libraries (e.g. date-fns) that operate on local time
// but should reflect the store's "now" instead of the device's "now".
export const getStoreNowDate = (countryCode, date = new Date()) => {
    const { year, month, day, hour, minute } = getDatePartsInTimezone(countryCode, date);
    return new Date(+year, +month - 1, +day, +hour, +minute);
};

// Formats a real Unix timestamp (UTC ms) using the store's timezone. Accepts
// the same `Intl.DateTimeFormat` options as `Date.prototype.toLocaleString`,
// but forces the `timeZone` to the one derived from the country code.
export const formatStoreDateTime = (countryCode, timestamp, options = {}) => {
    if (timestamp === undefined || timestamp === null || timestamp === '') return '';
    let date;
    if (timestamp instanceof Date) {
        date = timestamp;
    } else if (timestamp && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
    } else {
        date = new Date(+timestamp);
    }
    if (isNaN(date.getTime())) return '';

    const timeZone = getTimezoneByCountry(resolveStoreCountryCode(countryCode));
    const formatterOptions = { ...options, timeZone };

    return date.toLocaleString([], formatterOptions);
};

// IMPORTANT: this does NOT return a standard Unix timestamp. It returns a
// number of milliseconds that, when interpreted as UTC, yields the store's
// wall-clock time. Use together with `formatStoreTimestamp` (or any UTC-based
// formatter) so the value round-trips correctly.
//
// Example: if it is 16:22 in Singapore (UTC+8), Date.now() ~= 1764140520000
// (which is 08:22 UTC). getStoreTimestamp('SG') returns ~= 1764169320000
// (which is 16:22 UTC), so the store wall-clock time can be read back without
// knowing the timezone of the consumer.
export const getStoreTimestamp = (countryCode, date = new Date()) => {
    const { year, month, day, hour, minute } = getDatePartsInTimezone(countryCode, date);
    return Date.UTC(
        +year,
        +month - 1,
        +day,
        +hour,
        +minute,
        date.getUTCSeconds(),
        date.getUTCMilliseconds(),
    );
};

// Companion formatter for values produced by `getStoreTimestamp`. Because the
// stored ms already encodes store wall-clock time as if it were UTC, we force
// `timeZone: 'UTC'` to read the parts back without re-applying any offset.
export const formatStoreTimestamp = (timestamp, options = {}) => {
    if (timestamp === undefined || timestamp === null || timestamp === '') return '';
    const date = timestamp instanceof Date ? timestamp : new Date(+timestamp);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleString([], { ...options, timeZone: 'UTC' });
};

